import { useCallback } from 'react';
import config from '../config';

const useLobbyHandlers = (
  multiplayerState,
  gameSettings,
  gameLogic
) => {
  const {
    setGameMode,
    setLobbyCode,
    setPlayerName,
    setPlayers,
    setIsHost,
    setPlayerToken,
    resetMultiplayerState
  } = multiplayerState;

  const { questionCount, selectedCategory, selectedDifficulty } = gameSettings;

  const handleQuickPlay = useCallback(() => {
    console.log('[useLobbyHandlers] Switching to Quick Play mode');
    setGameMode('quickplay');
  }, [setGameMode]);

  const handleCreateLobby = useCallback(() => {
    console.log('[useLobbyHandlers] Switching to Create Lobby mode');
    setGameMode('create-lobby');
  }, [setGameMode]);

  const handleJoinLobby = useCallback(() => {
    console.log('[useLobbyHandlers] Switching to Join Lobby mode');
    setGameMode('join-lobby');
  }, [setGameMode]);

  const handleBackToMenu = useCallback(() => {
    console.log('[useLobbyHandlers] Returning to main menu');
    resetMultiplayerState();
    gameLogic.resetGame();
  }, [resetMultiplayerState, gameLogic]);

  const handleCreateLobbySubmit = useCallback(async (name) => {
    console.log('[useLobbyHandlers] Creating lobby with player name:', name);
    setPlayerName(name);
    setIsHost(true);
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/create-lobby`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerName: name,
          questionCount,
          category: selectedCategory,
          difficulty: selectedDifficulty
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        console.log('[useLobbyHandlers] Lobby created successfully:', data);
        console.log('[useLobbyHandlers] Storing player token:', data.token);
        setLobbyCode(data.lobbyCode);
        setPlayers(data.players);
        setPlayerToken(data.token);
        setGameMode('lobby-waiting');
      } else {
        console.error('[useLobbyHandlers] Failed to create lobby:', data.error);
        gameLogic.setError(data.error);
      }
    } catch (error) {
      console.error('[useLobbyHandlers] Error creating lobby:', error);
      gameLogic.setError('Failed to create lobby. Please try again.');
    }
  }, [setPlayerName, setIsHost, questionCount, selectedCategory, selectedDifficulty, setLobbyCode, setPlayers, setPlayerToken, setGameMode, gameLogic]);

  const handleJoinLobbySubmit = useCallback(async (code, name) => {
    console.log('[useLobbyHandlers] Joining lobby with code:', code, 'and name:', name);
    setPlayerName(name);
    setIsHost(false);
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/join-lobby`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lobbyCode: code,
          playerName: name
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        console.log('[useLobbyHandlers] Joined lobby successfully:', data);
        console.log('[useLobbyHandlers] Storing player token:', data.token);
        setLobbyCode(data.lobbyCode);
        setPlayers(data.players);
        setPlayerToken(data.token);
        setGameMode('lobby-waiting');
      } else {
        console.error('[useLobbyHandlers] Failed to join lobby:', data.error);
        gameLogic.setError(data.error);
      }
    } catch (error) {
      console.error('[useLobbyHandlers] Error joining lobby:', error);
      gameLogic.setError('Failed to join lobby. Please try again.');
    }
  }, [setPlayerName, setIsHost, setLobbyCode, setPlayers, setPlayerToken, setGameMode, gameLogic]);

  const handleStartLobbyGame = useCallback(async () => {
    const { lobbyCode, playerToken, playerName } = multiplayerState;
    console.log('[useLobbyHandlers] Starting lobby game', { lobbyCode, playerToken, playerName });
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/lobby/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lobbyCode })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        console.log('[useLobbyHandlers] Lobby game started successfully:', data);
        console.log('[useLobbyHandlers] Server response data keys:', Object.keys(data));
        console.log('[useLobbyHandlers] Server response gameId:', data.gameId);
        console.log('[useLobbyHandlers] Current playerToken:', playerToken);
        setPlayers(data.players);
        setGameMode('lobby-playing');
        // Start the game logic with multiplayer data
        console.log('[useLobbyHandlers] Calling startGame with:', { gameId: data.gameId, playerToken, playerName, mode: 'multiplayer' });
        gameLogic.startGame(data.gameId, playerToken, playerName, 'multiplayer');
      } else {
        console.error('[useLobbyHandlers] Failed to start lobby game:', data.error);
        gameLogic.setError(data.error);
      }
    } catch (error) {
      console.error('[useLobbyHandlers] Error starting lobby game:', error);
      gameLogic.setError('Failed to start game. Please try again.');
    }
  }, [multiplayerState, setPlayers, setGameMode, gameLogic]);

  const handleRefreshLobby = useCallback(async () => {
    const { lobbyCode, playerToken, playerName, gameMode } = multiplayerState;
    if (!lobbyCode) return;
    
    console.log('[useLobbyHandlers] Refreshing lobby status for:', lobbyCode);
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/lobby-status/${lobbyCode}`);
      const data = await response.json();
      
      if (response.ok) {
        console.log('[useLobbyHandlers] Lobby status refreshed:', data);
        setPlayers(data.players);
        
        // If game has started, switch to playing mode
        if (data.gameState === 'playing' && gameMode === 'lobby-waiting') {
          console.log('[useLobbyHandlers] Game has started, switching to playing mode');
          setGameMode('lobby-playing');
          // Pass the lobby game data to startGame for multiplayer
          console.log('[useLobbyHandlers] Calling startGame from refresh with:', { gameId: data.gameId, playerToken, playerName, mode: 'multiplayer' });
          gameLogic.startGame(data.gameId, playerToken, playerName, 'multiplayer');
        }
        
        // Always update players for live score updates during multiplayer game
        if (data.gameState === 'playing' && gameMode === 'lobby-playing') {
          console.log('[useLobbyHandlers] Updating live scores for multiplayer game');
          setPlayers(data.players);
        }
      } else {
        console.error('[useLobbyHandlers] Failed to refresh lobby status:', data.error);
      }
    } catch (error) {
      console.error('[useLobbyHandlers] Error refreshing lobby status:', error);
    }
  }, [multiplayerState, setPlayers, setGameMode, gameLogic]);

  return {
    handleQuickPlay,
    handleCreateLobby,
    handleJoinLobby,
    handleBackToMenu,
    handleCreateLobbySubmit,
    handleJoinLobbySubmit,
    handleStartLobbyGame,
    handleRefreshLobby
  };
};

export default useLobbyHandlers;
